/** O tabuleiro: terreno, ocupação e o que se enxerga de cada casa. */
import { isImpassable } from './content.js';
import { footprint, normalizeSize, positionKey, samePosition, SINGLE_TILE, spanDistance, } from './types.js';
const NO_EXITS = [];
/**
 * Tabela de saídas por casa, montada uma vez por mapa.
 *
 * O mapa não muda durante a partida e é o mesmo objeto em todo o estado, então
 * a tabela pode viver num cache fraco: sem ela, cada passo de Dijkstra
 * percorreria a lista de elos inteira.
 */
const exitCache = new WeakMap();
function exitTable(map) {
    const cached = exitCache.get(map);
    if (cached)
        return cached;
    const table = new Map();
    const link = (from, to) => {
        const key = positionKey(from);
        const list = table.get(key);
        if (list) {
            if (!list.some((p) => samePosition(p, to)))
                list.push(to);
        }
        else {
            table.set(key, [to]);
        }
    };
    for (const teleport of map.teleports ?? []) {
        if (samePosition(teleport.a, teleport.b))
            continue;
        link(teleport.a, teleport.b);
        link(teleport.b, teleport.a);
    }
    exitCache.set(map, table);
    return table;
}
/** Para onde as bocas de teleporte desta casa levam. Vazio no caso comum. */
export function teleportExits(map, from) {
    return exitTable(map).get(positionKey(from)) ?? NO_EXITS;
}
/** O passo entre duas casas é um salto de teleporte, e não uma caminhada? */
export function isTeleportStep(map, from, to) {
    return teleportExits(map, from).some((exit) => samePosition(exit, to));
}
export function inBounds(map, p) {
    return p.x >= 0 && p.y >= 0 && p.x < map.width && p.y < map.height;
}
export function terrainNameAt(map, p) {
    if (!inBounds(map, p))
        return 'abismo';
    const index = map.tiles[p.y * map.width + p.x] ?? 0;
    return map.terrainTypes[index] ?? 'planicie';
}
export function terrainAt(map, content, p) {
    return content.terrain.get(terrainNameAt(map, p));
}
/** As quatro casas vizinhas, sem diagonal — a grade do Fire Emblem é ortogonal. */
export function neighbors(p) {
    return [
        { x: p.x, y: p.y - 1 },
        { x: p.x - 1, y: p.y },
        { x: p.x + 1, y: p.y },
        { x: p.x, y: p.y + 1 },
    ];
}
/**
 * Para onde se pode dar um passo a partir daqui.
 *
 * As quatro vizinhas, mais o outro lado de qualquer teleporte que comece nesta
 * casa. É esta — e não `neighbors` — que o cálculo de movimento percorre.
 */
export function moveNeighbors(map, p) {
    const exits = teleportExits(map, p);
    const around = neighbors(p);
    return exits.length === 0 ? around : [...around, ...exits];
}
/** Todas as casas a uma distância entre min e max, em losango. */
export function tilesInRange(center, min, max) {
    const out = [];
    for (let dy = -max; dy <= max; dy++) {
        const budget = max - Math.abs(dy);
        for (let dx = -budget; dx <= budget; dx++) {
            const d = Math.abs(dx) + Math.abs(dy);
            if (d >= min && d <= max)
                out.push({ x: center.x + dx, y: center.y + dy });
        }
    }
    return out;
}
/**
 * As casas ao alcance de um corpo — a versão de `tilesInRange` para quem ocupa
 * mais de uma casa.
 *
 * Um dragão de quatro casas ameaça a partir de todas elas, e não só da âncora.
 * O losango vira o contorno do corpo engordado pelo alcance da arma.
 */
export function tilesInRangeOf(anchor, size, min, max) {
    const body = normalizeSize(size);
    if (body === SINGLE_TILE)
        return tilesInRange(anchor, min, max);
    const out = [];
    for (let dy = -max; dy < body.h + max; dy++) {
        for (let dx = -max; dx < body.w + max; dx++) {
            const p = { x: anchor.x + dx, y: anchor.y + dy };
            const d = spanDistance(anchor, body, p, SINGLE_TILE);
            if (d >= min && d <= max)
                out.push(p);
        }
    }
    return out;
}
export function buildOccupancy(units) {
    const map = new Map();
    for (const unit of units) {
        if (!unit.alive)
            continue;
        // Um corpo grande ocupa cada casa que cobre: é isso que o faz bloquear
        // passagem e ser encontrado por quem aponta para qualquer parte dele.
        for (const tile of footprint(unit.position, unit.size)) {
            map.set(positionKey(tile), { id: unit.id, team: unit.team });
        }
    }
    return map;
}
export function occupantAt(occupancy, p) {
    return occupancy.get(positionKey(p));
}
/**
 * Custo de entrar numa casa, ou `null` se não dá.
 *
 * Unidade inimiga bloqueia a passagem; aliada não — dá para atravessar quem
 * está do seu lado, e é isso que evita o engarrafamento em corredor que
 * arruinaria metade dos mapas.
 */
export function moveCost(map, content, occupancy, mover, to, size) {
    const playerSide = (t) => t === 'jogador' || t === 'aliado';
    // Um corpo grande só entra onde o corpo INTEIRO cabe. É por isso que um
    // dragão não passa por um corredor de uma casa — e é a licença que a regra
    // toma de propósito: o desenho promete um bicho que não cabe ali.
    let worst = 0;
    for (const tile of footprint(to, size)) {
        if (!inBounds(map, tile))
            return null;
        const terrain = terrainAt(map, content, tile);
        if (isImpassable(terrain))
            return null;
        const occupant = occupantAt(occupancy, tile);
        if (occupant && occupant.team !== mover && playerSide(occupant.team) !== playerSide(mover)) {
            return null;
        }
        worst = Math.max(worst, terrain?.cost ?? 1);
    }
    return worst;
}
/**
 * O corpo cabe aqui, com ninguém além de si mesmo por baixo?
 *
 * `moveCost` responde por onde se PASSA — atravessar um aliado é permitido.
 * Esta responde onde se PARA, que é a pergunta do destino de uma jogada.
 */
export function bodyFits(map, content, occupancy, at, selfId, size) {
    for (const tile of footprint(at, size)) {
        if (!inBounds(map, tile))
            return false;
        if (isImpassable(terrainAt(map, content, tile)))
            return false;
        const occupant = occupantAt(occupancy, tile);
        if (occupant && occupant.id !== selfId)
            return false;
    }
    return true;
}
//# sourceMappingURL=grid.js.map